import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip
} from '@mui/material';
import {
  Build as BuildIcon,
  Phone as PhoneIcon,
  MonetizationOn as MoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { updateEstimatedCost } from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord } from '../../utils/types';

interface EstimatedCostDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  userId: string;
  onSuccess: () => void;
}

const EstimatedCostDialog: React.FC<EstimatedCostDialogProps> = ({
  open,
  onClose,
  maintenance,
  userId,
  onSuccess
}) => {
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [requireCall, setRequireCall] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // تحديث القيم عند فتح النافذة
  useEffect(() => {
    if (open && maintenance) {
      setEstimatedCost(maintenance.estimatedCost || maintenance.initialCost || 0);
      setRequireCall(maintenance.requireCallBeforeMaintenance || false);
      setNotes('');
      setError(null);
    }
  }, [open, maintenance]);

  const handleSubmit = async () => {
    if (estimatedCost <= 0) {
      setError('يجب إدخال تكلفة أكبر من صفر');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateEstimatedCost(
        maintenance.id,
        estimatedCost,
        requireCall,
        userId,
        notes || undefined
      );
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating estimated cost:', error);
      setError('حدث خطأ أثناء تحديث التكلفة المقدرة');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  const costDifference = estimatedCost - (maintenance.initialCost || 0);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BuildIcon color="primary" />
          <Typography variant="h6">
            تحديث التكلفة المقدرة بعد الفحص
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات الطلب */}
          <Box mb={3} p={2} bgcolor="background.default" borderRadius={1}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات الطلب
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>العميل:</strong> {maintenance.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>نوع الجهاز:</strong> {maintenance.deviceType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة المبدئية:</strong> {formatCurrency(maintenance.initialCost || 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة الحالية:</strong> {formatCurrency(maintenance.estimatedCost || maintenance.initialCost || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* إدخال التكلفة المقدرة */}
          <TextField
            label="التكلفة المقدرة بعد الفحص"
            type="number"
            fullWidth
            value={estimatedCost || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setEstimatedCost(Math.max(0, value));
              setError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
              inputProps: { min: 0, step: 0.01 }
            }}
            helperText="التكلفة المقدرة بعد فحص الجهاز تفصيلياً"
            error={!!error}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* عرض الفرق في التكلفة */}
          {costDifference !== 0 && (
            <Box mb={2}>
              <Chip
                label={`${costDifference > 0 ? 'زيادة' : 'نقص'}: ${formatCurrency(Math.abs(costDifference))}`}
                color={costDifference > 0 ? 'warning' : 'success'}
                icon={<InfoIcon />}
              />
            </Box>
          )}

          {/* خيار الاتصال بالعميل */}
          <FormControlLabel
            control={
              <Checkbox
                checked={requireCall}
                onChange={(e) => setRequireCall(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon fontSize="small" />
                <Typography variant="body2">
                  يطلب العميل الاتصال به قبل بدء الصيانة
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {/* ملاحظات */}
          <TextField
            label="ملاحظات الفحص"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أضف ملاحظات حول نتائج الفحص والتكلفة المقدرة..."
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* معلومات إضافية */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>ملاحظة:</strong> إذا اخترت "يطلب العميل الاتصال"، سيتم إيقاف الصيانة حتى الحصول على موافقة العميل على التكلفة.
            </Typography>
          </Alert>

          {/* ملخص التكلفة */}
          {estimatedCost > 0 && (
            <Box p={2} bgcolor="success.light" borderRadius={1}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                ملخص التكلفة المقدرة
              </Typography>
              <Typography variant="body2" color="success.dark">
                <strong>التكلفة المقدرة:</strong> {formatCurrency(estimatedCost)}
              </Typography>
              {requireCall && (
                <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
                  ⚠️ سيتم إيقاف الصيانة حتى موافقة العميل
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || estimatedCost <= 0}
          startIcon={loading ? <CircularProgress size={20} /> : <BuildIcon />}
        >
          {loading ? 'جاري الحفظ...' : 'تحديث التكلفة'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EstimatedCostDialog;
